import classNames from 'classnames';
import {
    CSSProperties,
    ChangeEventHandler,
    FocusEventHandler,
    TextareaHTMLAttributes,
    forwardRef,
    useRef,
    useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

export interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    /**
     * Лейбл (описание)
     */
    label: string;
    /**
     * Нужно ли спрятать лейбл
     */
    hideLabel?: boolean;
    /**
     * Сообщение
     */
    message?: string;
    /**
     * Валиден ли инпут
     */
    valid?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, Props>(
    ({ id, label, hideLabel = false, valid = true, message, rows = 6, ...props }, ref) => {
        const labelEl = useRef<HTMLLabelElement>(null);
        const [focused, setFocused] = useState(false);
        const [innerValue, setInnerValue] = useState(props.value || props.defaultValue);
        const [labelElHeight, setLabelElHeight] = useState(0);
        const isFloatedLabelActive = focused || (typeof innerValue === 'string' ? innerValue.length > 0 : false);

        const onFocus: FocusEventHandler<HTMLTextAreaElement> = () => {
            setFocused(true);
        };

        const onBlur: FocusEventHandler<HTMLTextAreaElement> = () => {
            setFocused(false);
        };

        const onChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
            setInnerValue(event.target.value);
        };

        useIsomorphicLayoutEffect(() => {
            const onResize = () => {
                if (labelEl.current) {
                    setLabelElHeight(labelEl.current.offsetHeight);
                }
            };

            onResize();
            window.addEventListener('resize', onResize);

            return () => {
                window.removeEventListener('resize', onResize);
            };
        }, []);

        return (
            <div
                className={classNames('text-default input-group input-group--textarea', props.className, {
                    'floated-labels--active': isFloatedLabelActive,
                    'is-error': !valid,
                })}
                style={{ '--label-height': `${labelElHeight}px` } as CSSProperties}
            >
                <div className="textarea-wrapper">
                    <textarea
                        {...props}
                        ref={ref}
                        id={id}
                        className={classNames('form-control floated-labels', {
                            'floated-labels--active': isFloatedLabelActive,
                            'is-error': !valid,
                        })}
                        rows={rows}
                        onFocus={(event) => {
                            onFocus(event);
                            props.onFocus?.(event);
                        }}
                        onBlur={(event) => {
                            onBlur(event);
                            props.onBlur?.(event);
                        }}
                        onChange={(event) => {
                            onChange(event);
                            props.onChange?.(event);
                        }}
                    ></textarea>
                </div>
                <label
                    ref={labelEl}
                    htmlFor={id}
                    className={classNames('form-label text-s', { 'visually-hidden': hideLabel })}
                >
                    {label}
                </label>
                <div className="app-message" aria-live="polite" aria-describedby={id}>
                    {message}
                </div>
            </div>
        );
    },
);

Textarea.displayName = 'Textarea';

export default Textarea;
